import Vue from 'vue'
import Vuex from 'vuex'
import SelectionToolEngine from './plugins/SelectionToolEngine'

Vue.use(Vuex)
Vue.use(SelectionToolEngine)

export default new Vuex.Store({
  ste: SelectionToolEngine,
  state: {
    /**
     * Track the current application state, must be one of:
     * - loading - when the application is loading external files
     *   and resources.
     * - startup - the pre-quiz screen to introduce the process.
     * - active - actively involved in the process of answering questions.
     * - outcome - when enough questions have been completed to
     *   determine an outcome.
     * - error - when a technical problem has occurred (most likely
     *   a failure to load external data) and the application can't
     *   carry on.
     */
    currentState: 'loading',
    /**
     * RESPONSES will hold objects in the format of ...
     * {
     *   id: the id of the question,
     *   name: the name of this answer,
     *   value: the value of this answer,
     *   question: the name of the question
     * }
     */
    responses: [],
    /**
     * questionFlow will hold the questions that are part of the
     * current user journey through the tool.
     */
    questionFlow: [],
    /**
     * outcome holds the HTML string of the actual final result content
     */
    outcome: null,
    /**
     * These arrays have the values from SelectionToolData.js
     * passed into them to make it easier for Vue to interact
     * with it.
     */
    outcomes: [],
    textpanes: [],
    questions: [],
    /**
     * Step tracking...
     */
    steptracker: {
      current: 0,
      max: 0
    },
    hasGAtracker: false
  },
  mutations: {
    /**
     * Perform the mutation to add a question/value into the store.
     *
     * @param {object} state
     * @param {object} payload
     */
    RECORD_QUESTION_RESPONSE(state, payload) {
      state.responses.push(payload)
    },
    /**
     * Update the response to a question in the store.
     *
     * @param {object} state
     * @param {object} payload
     */
    UPDATE_QUESTION_RESPONSE(state, payload) {
      let responseIndex = null
      state.responses.forEach((response, index) => {
        if (response.id === payload.id) {
          responseIndex = index
        }
      })
      state.responses[responseIndex].name = payload.name
      state.responses[responseIndex].value = payload.value
      let actionsNeeded = this._vm.ste_actionsNeeded()
      /**
       * Are there unanswered questions in the flow? (later steps
       * that have not been addressed yet) if so remove them.
       */
      if (state.responses.length !== state.questionFlow.length) {
        state.questionFlow.forEach(q => {
          /**
           * If this question does NOT have a corresponding answer
           * force it's removal...
           */
          let hasFlowQuestion =
            typeof this.getters.getQuestionById(q.id) === 'object'
              ? true
              : false
          let hasResponse = this.getters.isQuestionAnswered(q.id)
          if (hasFlowQuestion !== hasResponse) {
            actionsNeeded.push(q.name)
          }
        })
      }
      if (actionsNeeded.length > 0) {
        actionsNeeded.forEach(name => {
          let theQuestion = this.getters.getQuestionByName(name)
          this.dispatch('removeFailedPrereq', theQuestion)
        })
        /**
         * Re-test the response to last question in the stack to make
         * sure we're still on a valid path and showing the right next
         * option.
         */
        let reviewResponse = state.responses[state.responses.length - 1]
        this.dispatch('setQuestionResponse', reviewResponse)
      }
    },
    /**
     * Deletes a response to a question from the store.
     *
     * @param {object} state
     * @param {object} payload
     */
    DELETE_QUESTION_RESPONSE(state, payload) {
      let responseIndex = null
      state.responses.forEach((response, index) => {
        if (response.id === payload.id) {
          responseIndex = index
        }
      })
      if (responseIndex) {
        state.responses.splice(responseIndex)
      }
    },
    /**
     * Remove all the responses that have been submitted (to
     * reset the application).
     *
     * @param {object} state
     */
    PURGE_QUESTION_RESPONSES(state) {
      while (state.responses.length > 0) {
        state.responses.pop()
      }
    },
    /**
     * Remove all the questions (except the default first one)
     * from the question flow.
     *
     * @param {object} state
     */
    PURGE_QUESTION_FLOW(state) {
      // Leave the first (default) question in place...
      while (state.questionFlow.length > 1) {
        state.questionFlow.pop()
      }
    },
    /**
     * Remove the last response that was submitted (to step
     * backwards through the question flow).
     *
     * @param {object} state
     */
    REMOVE_LAST_QUESTION_RESPONSE(state) {
      state.responses.pop()
      state.questionFlow.pop()
      this.dispatch('setSteptrackerCurrent', state.responses.length)
      this.dispatch('setSteptrackerMax', state.questionFlow.length)
    },
    /**
     * @TODO: Make this do a thing
     * Remove a question from the question flow
     *
     * @param {object} state
     * @param {object} payload
     */
    DELETE_QUESTION_FROM_FLOW(state, payload) {
      let questionIndex = false
      state.questionFlow.forEach((question, index) => {
        if (question.id === payload.id) {
          questionIndex = index
        }
      })
      if (questionIndex) {
        state.questionFlow.splice(questionIndex)
      }
    },
    /**
     * Change the current state of the application.
     *
     * @param {object} state
     * @param {string} name
     */
    SET_STATE(state, name) {
      state.currentState = name
    },
    /**
     * Set the number of questions that have been answered.
     *
     * @param {object} state
     * @param {int} number
     */
    SET_STEPTRACKER_CURRENT(state, number) {
      state.steptracker.current = number
    },
    /**
     * Set the number of questions that are anticipated to
     * be part of the process.
     *
     * @param {object} state
     * @param {int} number
     */
    SET_STEPTRACKER_MAX(state, number) {
      state.steptracker.max = number
    },
    /**
     *
     * @param {object} state
     * @param {*} id
     */
    SWITCH_TO_QUESTION(state, id) {
      let theQuestion = this.getters.getQuestionById(id)
      if (theQuestion) {
        state.questionFlow.push(theQuestion)
      } else {
        return false
      }
    },
    /**
     * Set the outcome state for the quiz.
     *
     * @param {object} state
     * @param {object} payload
     */
    SET_OUTCOME(state, payload) {
      state.outcome = payload.name
    },
    /**
     * These three mutations (SYNC_OUTCOMES, SYNC_TEXTPANES,
     * and SYNC_QUESTIONS) are used to clone static content
     * from selectionToolData.js into the VueX store.
     *
     * @param {object} state
     * @param {object} payload
     */
    SYNC_OUTCOMES(state, payload) {
      state.outcomes = payload
    },
    SYNC_TEXTPANES(state, payload) {
      state.textpanes = payload
    },
    SYNC_QUESTIONS(state, payload) {
      state.questions = payload
    }
  },
  actions: {
    /**
     * When a question is answered, add it to the responses kept
     * in the store.
     *
     * @param {object} commit
     * @param {object} payload - the question response information
     *   to be added to the responses array.
     */
    setQuestionResponse({ commit, state }, payload) {
      /**
       * Determine if we need to add a new response or update an
       * existing one.
       */
      let alreadyHasResponse = state.responses.filter(
        response => response.id === payload.id
      )
      if (!alreadyHasResponse.length > 0) {
        commit('RECORD_QUESTION_RESPONSE', payload)
      } else {
        commit('UPDATE_QUESTION_RESPONSE', payload)
      }
      /**
       * Use the STE to tell us what to do next...
       */
      let nextStep = this._vm.ste_nextStep(payload.question)
      /**
       * A `nextStep` with .options tells us we're moving on to
       * another question, determine if it's already in the
       * questionFlow and add it if not.
       */
      if (typeof nextStep.options !== 'undefined') {
        // check if this question is already in the array or not...
        let isQuestionInFlow = state.questionFlow.filter(
          question => question.id === nextStep.id
        ).length
        if (!isQuestionInFlow) {
          this.dispatch('switchToQuestion', nextStep.id)
          this.dispatch('sendAnalyticsRequest', {
            type: 'question',
            data: payload
          })
        }
      } else {
        /**
         * Otherwise, a `nextStep` without .options tells us we're
         * moving to an outcome and returns the outcome HTML for us
         * to display.
         */
        this.dispatch('setOutcome', nextStep)
        this.dispatch('sendAnalyticsRequest', {
          type: 'outcome',
          data: nextStep
        })
      }
      // Update the step tracker?
      this.dispatch('setSteptrackerCurrent', state.responses.length)
      this.dispatch('setSteptrackerMax', state.questionFlow.length)
    },
    /**
     * Step back one question at a time through the questions
     * (this discards later answers).
     * @param {object} commit
     */
    backQuestionResponse({ commit }) {
      this.dispatch('sendAnalyticsRequest', { type: 'back', data: 'back' })
      commit('REMOVE_LAST_QUESTION_RESPONSE')
    },
    /**
     * Switch us to an OUTCOME screen.
     *
     * @param {object} commit
     * @param {object} payload
     */
    setOutcome({ commit }, payload) {
      commit('SET_STATE', 'outcome')
      commit('SET_OUTCOME', payload)
    },
    /**
     * Trigger the application to change state to one of the options
     * documented above in the comments around the store.state code.
     *
     * @param {object} commit
     * @param {string} name - the name for the application state
     * that we want to trigger.
     */
    setState({ commit }, name) {
      commit('SET_STATE', name)
      this.dispatch('sendAnalyticsRequest', { type: 'state', data: name })
    },
    /**
     * Wipe the slate clean and reset everything to the default state
     * for starting the selection process.
     *
     * @param {object} commit
     */
    resetApplication({ commit }) {
      commit('PURGE_QUESTION_RESPONSES')
      commit('PURGE_QUESTION_FLOW')
      commit('SET_STATE', 'startup')
      commit('SET_STEPTRACKER_CURRENT', 0)
      commit('SET_STEPTRACKER_MAX', 1)
      this.dispatch('sendAnalyticsRequest', { type: 'reset', data: 'reset' })
    },
    /**
     * Set the current number of answered questions used to
     * tell the Step Tracker component how far into the process
     * the user is.
     *
     * @param {object} commit
     * @param {int} number - the number of questions that have
     * been answered.
     */
    setSteptrackerCurrent({ commit }, number) {
      commit('SET_STEPTRACKER_CURRENT', number)
    },
    /**
     * Set the anticipated maximum number of questions necessary
     * to complete the current process and make that number
     * available to the Step Tracker component.
     *
     * @param {object} commit
     * @param {int} number - the anticipated number of questions in
     * the current question flow.
     */
    setSteptrackerMax({ commit }, number) {
      commit('SET_STEPTRACKER_MAX', number)
    },
    /**
     * Pushes a question into the question flow (making it the new
     * active item).
     *
     * @param {object} commit
     * @param {*} id
     */
    switchToQuestion({ commit }, id) {
      commit('SWITCH_TO_QUESTION', id)
    },
    /**
     * Remove an item that has failed the prereq checks...
     *
     * @param {object} commit
     * @param {object} payload
     */
    removeFailedPrereq({ commit }, payload) {
      commit('DELETE_QUESTION_FROM_FLOW', payload)
      commit('DELETE_QUESTION_RESPONSE', payload)
    },
    /**
     * These three actions (syncOutcomes, syncQuestions, and
     * syncTextpanes) are all used to sync static data (provided
     * in the selectionToolData.js file) to the VueX store for
     * easier/reactive use by Vue components.
     *
     * @param {object} commit
     * @param {object} payload
     */
    syncOutcomes({ commit }, payload) {
      commit('SYNC_OUTCOMES', payload)
    },
    syncQuestions({ commit }, payload) {
      commit('SYNC_QUESTIONS', payload)
    },
    syncTextpanes({ commit }, payload) {
      commit('SYNC_TEXTPANES', payload)
    },
    /**
     * This is a helper to push data to GTM/Analytics for better
     * reporting on user needs and choices.
     */
    sendAnalyticsRequest({ state }, payload) {
      let pushNewData = false
      const analyticsStruct = {
        hitType: 'event',
        eventCategory: 'pmlst'
      }
      const eventType = payload.type
      const eventData = payload.data

      // Only try to push data if there is a dataLayer to communicate with
      if (typeof window.dataLayer !== 'undefined') {
        // Build the data to push to GA
        if (eventType === 'state' && eventData === 'active') {
          analyticsStruct.eventAction = 'start-quiz'
          analyticsStruct.eventLabel = 'start-quiz'
          pushNewData = true
        }
        if (eventType === 'question') {
          analyticsStruct.eventAction = `${eventData.question}=${eventData.value}`
          analyticsStruct.eventLabel = 'answered-question'
          pushNewData = true
        }
        if (eventType === 'outcome') {
          let answersAsString = ''
          state.responses.forEach(item => {
            answersAsString += `q${item.id}=${item.value},`
          })
          analyticsStruct.eventAction = answersAsString
          analyticsStruct.eventLabel = eventData.name
          pushNewData = true
        }
        if (eventType === 'reset') {
          analyticsStruct.eventAction = 'reset-quiz'
          analyticsStruct.eventLabel = 'reset-quiz'
          pushNewData = true
        }
        if (eventType === 'back') {
          const destinationQuestion =
            state.questionFlow[state.questionFlow.length - 2].name
          analyticsStruct.eventAction = destinationQuestion
          analyticsStruct.eventLabel = 'back-to-question'
          pushNewData = true
        }
        if (eventType === 'custom') {
          analyticsStruct.eventAction = eventData.action
          analyticsStruct.eventLabel = eventData.label
          pushNewData = true
        }
        // Send to GTM to relay to GA4
        if (pushNewData) {
          // Analytics struct has all the data to send
          // but it needs to be reformatted for GTM to
          // receive it...
          const dataToSend = {
            event: analyticsStruct.eventCategory,
            eventAction: analyticsStruct.eventAction,
            eventLabel: analyticsStruct.eventLabel
          }
          window.dataLayer.push(dataToSend)
          // console.log('analytics struct', analyticsStruct)
          // console.log('send data to GTM', dataToSend)
        }
      }
    }
  },
  getters: {
    /**
     * Get the current application state (loading, active, startup, completed)
     *
     * @param {object} state - a reference to the state Object overall.
     * @returns {string} - the name of the current application state.
     */
    getApplicationState: state => {
      return state.currentState
    },
    /**
     * @param {object} state
     * @param {string} id
     * @returns {boolean}
     */
    isQuestionAnswered: state => id => {
      let result = state.responses.filter(response => response.id === id)
      return result.length > 0 ? true : false
    },
    /**
     * Read the value for a particular response from the VueX store.
     *
     * @param {object} state - a reference to the state Object
     * @param {string} id - The ID of the question to get the answer for.
     * @returns {(*|null)} - returns that response value or NULL if
     *   there is no corresponding value.
     */
    getResponseForQuestion: state => id => {
      let result = state.responses.filter(response => response.id === id)
      return result.length > 0 ? result[0].value : null
    },
    /**
     * Read the step tracker from the VueX store.
     *
     * @param {object} state - a reference to the state Object overall.
     * @returns {object} - returns an object with the currently completed
     *   number of steps and the maximum number of steps in the current
     *   process flow.
     */
    getSteptrackerState: state => {
      return state.steptracker
    },
    /**
     * Get all responses currently submitted to determine if this is a complete
     * pass through the flow.
     *
     * @param {object} state - a reference to the state Object overall.
     * @returns {array} - an array of Objects detailing the questions that
     *   have been answered and what their individual responses are.
     */
    getAllResponses: state => {
      return state.responses
    },
    /**
     * Gets a Question from the store by passing the NAME for it.
     *
     * @param {object} state
     * @param {string} name
     * @returns {object|false} - a Question object from the store or
     *   FALSE if there is no matching object.
     */
    getQuestionByName: state => name => {
      let result = state.questions.filter(question => question.name === name)
      return result.length > 0 ? result[0] : false
    },
    /**
     * Get a Question from the store by passing the ID for it.
     *
     * @param {object} state - a reference to the state Object overall.
     * @param {string?} id - the ID for the question to retrieve.
     * @returns {object|false} - a Question object from the store or
     *   FALSE if there is no matching object.
     */
    getQuestionById: state => id => {
      let result = state.questions.filter(question => question.id === id)
      return result.length > 0 ? result[0] : false
    },
    /**
     * Gets the list of active questions within this quiz.
     *
     * @param {object} state - a reference to the state Object overall.
     */
    getQuestionFlow: state => {
      return state.questionFlow
    },
    /**
     * Get a Textpane from the store by passing the ID for it.
     *
     * @param {object} state - a reference to the state Object overall.
     * @param {string?} id - the ID for the Textpane to retrieve.
     * @returns {object|false} - a Textpane object from the store or
     *   FALSE if there is no matching object.
     */
    getTextpaneById: state => id => {
      /**
       * @todo: UNTESTED, needs to be worked on for real.
       */
      let result = state.textpanes.filter(textpane => textpane.id === id)
      return result.length > 0 ? result[0] : false
    },
    /**
     * Get the outcome content for display on the page.
     *
     * @param {object} state
     */
    getOutcome: state => {
      return state.outcome
    }
  }
})
